import { Dictionary, sortWith, ascend, prop, uniqBy } from 'ramda'
import {
  useContext,
  readonly,
  ref,
  computed,
  Ref,
} from '@nuxtjs/composition-api'
import Vue from 'vue'
import debounce from 'lodash.debounce'
import { useConfig } from '../useConfig'
import { useLoading } from '../ui/useLoading'
import { useWebNode } from '../useWebNode'
import { useProductStockRefresh } from './useProduct'
import project from '~/project.config.js'
import {
  Product,
  ProductField,
  ProductConnectionType,
  ProductReview,
} from '~/types/product'

export enum SPECIFICATION_CODES {
  Stock = 'stock',
  Brand = 'brand',
  Group = 'group',
  Categories = 'categories',
  ScanCode = 'scanCode',
}

const COMMON_CONNECTION_TYPES = (project.product?.details
  ?.commonConnectionTypes ?? []) as ProductConnectionType[]
const VARIANT_CONNECTION_TYPES = (project.product?.details
  ?.variantConnectionTypes ?? []) as ProductConnectionType[]

export const useProductDetail = () => {
  const {
    app,
    error,
  } = useContext()
  const {
    ensureRoot,
    webNodeNamesMap
  } = useWebNode()
  const { i18n } = app
  const { config } = useConfig()
  const loading = useLoading()
  const connectionsLoading = useLoading()
  const reviewsLoading = useLoading()
  const product = ref<Product | null>(null)
  const connections = ref<Dictionary<Product[]>>({})
  const reviews = ref<ProductReview[]>([])
  const reviewsTotal = ref(0)
  const rating = ref(0)
  const colorVariants = ref<Product[]>([])
  const sizeVariants = ref<Product[]>([])
  const variantVariants = ref<Product[]>([])

  const variantProduct = ref<Product | null>(null)
  const selectedProduct = computed(
    () => variantProduct.value ?? product.value ?? ({} as Product)
  )
  const selectVariantProduct = async (product: Product) => {
    variantProduct.value = product
    await onSelectVariantProduct(product)
  }

  const onSelectVariantProduct = debounce(async (product: Product) => {
    // refresh common connections
    const reloadedConnections = await app.$api.product.getConnections(
      product.id,
      COMMON_CONNECTION_TYPES
    )
    COMMON_CONNECTION_TYPES.forEach((type: string) => {
      // Vue.set() is not needed in vue3
      Vue.set(connections.value, type, reloadedConnections[type] ?? [])
    })

    // refresh reviews
    await getReviews()
  }, 800)


  const { productLoadedFromServer, onProductsLoaded, refreshStocks } =
    useProductStockRefresh(product)

  const connectionsArray = computed(() =>
    Object.keys(connections.value)
      .map((key) => connections.value[key])
      .reduce((list, products) => list.concat(products), [])
  )
  const {
    onProductsLoaded: onConnectionsLoaded,
    refreshStocks: refreshConnctionsStocks,
  } = useProductStockRefresh(connectionsArray)

  const getProduct = async (url: string, currentWebNodeId?: number) => {
    await loading.scope(async () => {
      await ensureRoot()

      const result = await app.$api.product.getByUrl(url, currentWebNodeId, {
        webNodeNamesMap: webNodeNamesMap.value!
      })

      if (!result || !result.isViewable) {
        error({
          statusCode: 404,
        })
      } else {
        product.value = result
      }

      onProductsLoaded()
    })
  }

  const getSpecificationI18nLabel = (code: SPECIFICATION_CODES) => {
    return `${i18n.t(`product.specification.${code}`)}`
  }

  const specifications = computed(() => {
    const result: ProductField[] = []
    const specifications = config.value?.productSpecifications
    const fields = selectedProduct.value?.fields

    if (fields && specifications) {
      fields.forEach((item) => {
        if (
          specifications[item.code.toLowerCase()] ||
          specifications[item.label]
        ) {
          result.push({
            code: item.code,
            label: item.label,
            value: item.value,
          })
        }
      })
    }

    if (selectedProduct.value) {
      result.push({
        code: SPECIFICATION_CODES.Stock,
        label: getSpecificationI18nLabel(SPECIFICATION_CODES.Stock),
        value: selectedProduct.value.stockQuantity,
      })
      if (selectedProduct.value.brand?.name) {
        result.push({
          code: SPECIFICATION_CODES.Brand,
          label: getSpecificationI18nLabel(SPECIFICATION_CODES.Brand),
          value: selectedProduct.value.brand.name,
        })
      }
      if (product.value?.activeWebNodes?.length) {
        result.push({
          code: SPECIFICATION_CODES.Categories,
          label: getSpecificationI18nLabel(SPECIFICATION_CODES.Categories),
          value: product.value.activeWebNodes.map((webNode) => webNode.name),
        })
      }
      if (selectedProduct.value.scanCode) {
        result.push({
          code: SPECIFICATION_CODES.ScanCode,
          label: getSpecificationI18nLabel(SPECIFICATION_CODES.ScanCode),
          value: selectedProduct.value.scanCode,
        })
      }
      if (selectedProduct.value.groupName) {
        result.push({
          code: SPECIFICATION_CODES.Group,
          label: getSpecificationI18nLabel(SPECIFICATION_CODES.Group),
          value: selectedProduct.value.groupName,
        })
      }
    }

    return result
  })

  const getConnections = async () => {
    await connectionsLoading.scope(async () => {
      if (!product.value) return

      const types = product.value.isVirtual
        ? VARIANT_CONNECTION_TYPES
        : COMMON_CONNECTION_TYPES
      if (!types.length) return

      const groups = await app.$api.product.getConnections(
        product.value.id,
        types
      )

      sizeVariants.value = groups[ProductConnectionType.Size]

      colorVariants.value =
        groups[ProductConnectionType.Color]?.reduce((prev, current) => {
          return prev.filter((item) => item.id === current.id).length
            ? prev
            : [...prev, current]
        }, [] as Product[]) ?? []

      variantVariants.value = sortWith(
        [ascend(prop('priceInclTax')), ascend(prop('name'))],
        groups[ProductConnectionType.Variant] ?? []
      )

      delete groups[ProductConnectionType.Color]
      delete groups[ProductConnectionType.Size]

      connections.value = groups
      onConnectionsLoaded()
    })
  }

  const getReviews = () => {
    return reviewsLoading.scope(async () => {
      if (!selectedProduct.value) return
      const result = await app.$api.product.getReviews({
        productId: selectedProduct.value.id,
        limit: 2,
      })
      reviews.value = result?.reviews ?? []
      rating.value = result?.averageRating ?? 0
      reviewsTotal.value = result?.totalCount ?? 0
    })
  }

  const addViewedProduct = async () => {
    if (!product.value) return

    await app.$api.product.addViewedProduct(
      product.value.id,
      product.value.brand?.id
    )
  }

  return {
    loading: readonly(loading.value),
    product: readonly(product),
    productLoadedFromServer,
    connections: readonly(connections),
    reviews: readonly(reviews),
    rating: readonly(rating),
    reviewsTotal: readonly(reviewsTotal),
    specifications,
    sizeVariants,
    colorVariants,
    variantVariants,
    connectionsLoading: readonly(connectionsLoading.value),
    reviewsLoading: readonly(reviewsLoading.value),
    variantProduct,
    selectedProduct,

    getProduct,
    selectVariantProduct,
    refreshStocks,
    getConnections,
    refreshConnctionsStocks,
    getReviews,
    addViewedProduct,
  }
}

/*
 * Viewed product list
 */
export const useViewedProducts = () => {
  const viewedProducts = ref<Product[]>([])

  const { app } = useContext()

  const getViewedProducts = async () => {
    viewedProducts.value = await app.$api.product.getViewedProducts()
  }

  return {
    viewedProducts: readonly(viewedProducts),
    getViewedProducts,
  }
}

export const useProductVariants = (
  originalVariants: Product[],
  selectedVariant: Ref<Product>,
  _options?: {
    preSelectedId?: number
    sort?: (a: Product, b: Product) => number
    select?: (...args: any) => any
  }
) => {
  const options = Object.assign(
    {
      sort: (a: Product, b: Product) => {
        if (a.priceInclTax === b.priceInclTax) {
          return a.name > b.name ? 1 : -1
        }
        return a.priceInclTax - b.priceInclTax
      },
    },
    _options
  )

  const variants = computed(() => {
    if (!originalVariants.length) return []

    return uniqBy((product) => product.id, originalVariants).sort(options.sort)
  })

  const select = (product: Product) => {
    if (selectedVariant.value?.id === product.id) return
    options.select?.(product)
  }

  const selectDefault = () => {
    if (!variants.value.length) return

    let matched: Product | undefined
    if (options.preSelectedId) {
      matched = variants.value.find(
        (product) => product.id === options.preSelectedId
      )
    }
    if (!matched) {
      matched = variants.value.find((product) => product.isOrderable)
    }
    if (matched) {
      select(matched)
    } else {
      select(variants.value[0])
    }
  }

  return {
    variants,
    selectDefault,
  }
}
